import {
  AfterViewInit,
  Component,
  inject,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Chart, Plugin, registerables } from 'chart.js';
import { environment } from '@myenv/environment';
import { DemoDataService } from '@shared/demo-data/demo-data.service';

/** Labels centered above each vertical bar (value end). */
const verticalBarOutsideValueLabels: Plugin<'bar'> = {
  id: 'verticalBarOutsideValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    ctx.save();
    ctx.font =
      '600 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = '#475569';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) {
        return;
      }
      meta.data.forEach((element, index) => {
        const raw = dataset.data[index];
        const value = typeof raw === 'number' ? raw : Number(raw);
        if (raw == null || Number.isNaN(value)) {
          return;
        }
        const { x, y } = element.getProps(['x', 'y'], true);
        ctx.fillText(String(value), x, y - 4);
      });
    });
    ctx.restore();
  },
};

/** Labels to the right of each horizontal bar (value end). */
const horizontalBarOutsideValueLabels: Plugin<'bar'> = {
  id: 'horizontalBarOutsideValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    ctx.save();
    ctx.font =
      '600 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = '#475569';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) {
        return;
      }
      meta.data.forEach((element, index) => {
        const raw = dataset.data[index];
        const value = typeof raw === 'number' ? raw : Number(raw);
        if (raw == null || Number.isNaN(value)) {
          return;
        }
        const { x, y } = element.getProps(['x', 'y'], true);
        ctx.fillText(String(value), x + 6, y);
      });
    });
    ctx.restore();
  },
};

@Component({
  selector: 'app-fleet-overview',
  templateUrl: './fleet-overview.component.html',
  styleUrls: ['./fleet-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FleetOverviewComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('donutCanvas') donutRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('productCanvas') productRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('fulfillmentCanvas') fulfillmentRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('configCanvas') configRef: ElementRef<HTMLCanvasElement>;

  fleetOptions = ['Metro Fleet', 'North Region', 'South Region'];
  selectedFleet = 'Metro Fleet';
  fleetDropdownOpen = false;

  totalDevices = 527;
  deviceStatusSummary = {
    good: 426,
    atRisk: 69,
    attention: 32,
  };

  productBreakdown = [
    { code: 'DL972', count: 219, pct: 42, color: '#1e3a5f' },
    { code: 'DL974', count: 183, pct: 35, color: '#3b7dd6' },
    { code: 'DCM970', count: 125, pct: 23, color: '#14b8a6' },
  ];

  fulfillmentMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  fulfillmentShipped = [118, 124, 132, 128, 137, 135];
  fulfillmentActivated = [68, 72, 78, 80, 85, 87];

  /** Config mix per product code; keys match `productBreakdown[].code`. */
  configDistributionByProduct: Record<
    string,
    { label: string; count: number; color: string }[]
  > = {
    DL972: [
      { label: 'FP. 2012', count: 221, color: '#1e3a5f' },
      { label: 'FL. 901', count: 43, color: '#7eb8ff' },
      { label: 'FL. 3101', count: 28, color: '#f59e0b' },
      { label: 'SL. 4102', count: 17, color: '#ec4899' },
      { label: 'No Config', count: 10, color: '#94a3b8' },
    ],
    DL974: [
      { label: 'FP. 2012', count: 62, color: '#1e3a5f' },
      { label: 'FL. 901', count: 58, color: '#7eb8ff' },
      { label: 'FL. 3101', count: 35, color: '#f59e0b' },
      { label: 'SL. 4102', count: 22, color: '#ec4899' },
      { label: 'No Config', count: 6, color: '#94a3b8' },
    ],
    DCM970: [
      { label: 'FP. 2012', count: 38, color: '#1e3a5f' },
      { label: 'FL. 901', count: 29, color: '#7eb8ff' },
      { label: 'FL. 3101', count: 33, color: '#f59e0b' },
      { label: 'SL. 4102', count: 18, color: '#ec4899' },
      { label: 'No Config', count: 7, color: '#94a3b8' },
    ],
  };

  selectedProductCode = '';

  healthCards = [
    {
      key: 'communicating',
      label: 'Communicating',
      value: 369,
      pct: 70,
      valueColor: '#10b981',
      border: '#9fe3cd',
      iconBg: '#e8fdf6',
      iconClass: 'fa fa-wifi',
      iconUrl: 'assets/img/reports-svg-icons/icon-wifi.svg',
    },
    {
      key: 'notCommunicating',
      label: 'Not Communicating',
      value: 89,
      pct: 17,
      valueColor: '#f59e0b',
      border: '#ffd9a3',
      iconBg: '#fef5e7',
      iconClass: 'fa fa-exclamation-triangle',
      iconUrl: 'assets/img/reports-svg-icons/icon-warning.svg',
    },
    {
      key: 'disconnected',
      label: 'Disconnected',
      value: 37,
      pct: 7,
      valueColor: '#f43e5c',
      border: '#ffc1c8',
      iconBg: '#fee7eb',
      iconClass: 'fa fa-chain-broken',
      iconUrl: 'assets/img/reports-svg-icons/icon-wifi-off.svg',
    },
    {
      key: 'never',
      label: 'Never Communicated',
      value: 32,
      pct: 6,
      valueColor: '#65758b',
      border: '#cbd5e1',
      iconBg: '#f0f2f4',
      iconClass: 'fa fa-ban',
      iconUrl: 'assets/img/reports-svg-icons/icon-signal-disconnected.svg',
    },
  ];

  deviceSearch = '';
  devicePage = 1;
  devicePageSize = 5;
  isSidebarOpen = false;
  selectedDevice: any = null;

  allDevices = [
    {
      serial: '2520472042',
      iccid: '8914800000123456793',
      lastContact: '01/09/2026 10:58:53 AM',
      configAge: '259 days ago',
      event: 'Heartbeat',
      tripStatus: 'TripEnd',
      signalStrength: 80,
      batteryLevel: 60,
      location: 'San Diego, CA',
      imei: '916949369478154',
      configVersion: 'GSK5ASW226',
      firmwareVersion: '31D6510810',
      productCode: 'DL972',
      uptime: '5d 3h',
      recentActivity: [
        { title: 'Heartbeat Signal', time: '2 mins ago' },
        { title: 'Configuration updated', time: '20 mins ago' },
        { title: 'Firmware check', time: '3 hours ago' },
      ],
    },
    {
      serial: '2520472088',
      iccid: '8914800000123456801',
      lastContact: '01/08/2026 04:12:11 PM',
      configAge: '18 days ago',
      event: 'Ignition ON',
      tripStatus: 'TripStart',
      signalStrength: 76,
      batteryLevel: 58,
      location: 'Austin, TX',
      imei: '916949369478155',
      configVersion: 'GSK5ASW219',
      firmwareVersion: '31D6510808',
      productCode: 'DL974',
      uptime: '2d 10h',
      recentActivity: [
        { title: 'Ignition ON', time: '6 mins ago' },
        { title: 'Config sync', time: '35 mins ago' },
        { title: 'Heartbeat Signal', time: '1 hour ago' },
      ],
    },
    {
      serial: '2520472091',
      iccid: '8914800000123456810',
      lastContact: '01/07/2026 09:22:40 AM',
      configAge: '3 hours ago',
      event: 'Ignition OFF',
      tripStatus: 'TripIdle',
      signalStrength: 88,
      batteryLevel: 72,
      location: 'Phoenix, AZ',
      imei: '916949369478156',
      configVersion: 'GSK5ASW212',
      firmwareVersion: '31D6510806',
      productCode: 'DCM970',
      uptime: '7d 4h',
      recentActivity: [
        { title: 'Ignition OFF', time: '10 mins ago' },
        { title: 'Heartbeat Signal', time: '42 mins ago' },
        { title: 'Firmware check', time: '4 hours ago' },
      ],
    },
    {
      serial: '2520472104',
      iccid: '8914800000123456822',
      lastContact: '01/05/2026 11:05:18 AM',
      configAge: '20 days ago',
      event: 'Low Battery',
      tripStatus: 'LowBatt',
      signalStrength: 62,
      batteryLevel: 29,
      location: 'Reno, NV',
      imei: '916949369478157',
      configVersion: 'GSK5ASW206',
      firmwareVersion: '31D6510803',
      productCode: 'DL974',
      uptime: '1d 7h',
      recentActivity: [
        { title: 'Low Battery', time: '8 mins ago' },
        { title: 'Heartbeat Signal', time: '1 hour ago' },
        { title: 'Config update', time: '9 hours ago' },
      ],
    },
    {
      serial: '2520472116',
      iccid: '8914800000123456834',
      lastContact: '12/02/2025 11:45:32 AM',
      configAge: '12 hours ago',
      event: 'Heartbeat',
      tripStatus: 'TripEnd',
      signalStrength: 70,
      batteryLevel: 52,
      location: 'Seattle, WA',
      imei: '916949369478158',
      configVersion: 'GSK5ASW198',
      firmwareVersion: '31D6510798',
      productCode: 'DL972',
      uptime: '3d 1h',
      recentActivity: [
        { title: 'Heartbeat Signal', time: '12 mins ago' },
        { title: 'Configuration updated', time: '2 hours ago' },
        { title: 'Firmware check', time: '7 hours ago' },
      ],
    },
  ];

  private donutChart: any;
  private productChart: any;
  private fulfillmentChart: any;
  private configChart: any;


  private title = inject(Title);
  private demoData = inject(DemoDataService);

  ngOnInit() {
    Chart.register(...registerables);
    this.title.setTitle('Fleet Overview' + environment.title_text);
    // The static showcase data above is shown to the demo user (customerId 10
    // / "Demo") even after the fleet API is integrated. Real accounts should
    // load API data in the branch below so demo data is never overwritten.
    // TODO: replace this branch with the real fleet-overview API calls.
    if (!this.demoData.isDemoUser()) {
      // e.g. this.allDevices = apiDevices; this.productBreakdown = ...;
    }
    this.selectedDevice = this.allDevices[0];
    this.selectedProductCode = this.productBreakdown[0]?.code ?? '';
  }

  get configDistribution() {
    return (
      this.configDistributionByProduct[this.selectedProductCode] ??
      this.configDistributionByProduct[this.productBreakdown[0]?.code] ??
      []
    );
  }

  selectProductCode(code: string) {
    if (code === this.selectedProductCode) {
      return;
    }
    this.selectedProductCode = code;
    this.updateConfigChart();
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => this.initCharts());
  }

  ngOnDestroy() {
    this.donutChart?.destroy();
    this.productChart?.destroy();
    this.fulfillmentChart?.destroy();
    this.configChart?.destroy();
  }

  get filteredDevices() {
    const q = this.deviceSearch.trim().toLowerCase();
    if (!q) {
      return this.allDevices;
    }
    return this.allDevices.filter(
      (d) =>
        d.serial.toLowerCase().includes(q) ||
        d.iccid.toLowerCase().includes(q) ||
        d.event.toLowerCase().includes(q)
    );
  }

  get pagedDevices() {
    const start = (this.devicePage - 1) * this.devicePageSize;
    return this.filteredDevices.slice(start, start + this.devicePageSize);
  }

  get devicePageCount(): number {
    return Math.max(
      1,
      Math.ceil(this.filteredDevices.length / this.devicePageSize)
    );
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.devicePageCount }, (_, i) => i + 1);
  }

  get fulfillmentShippedSum(): number {
    return this.fulfillmentShipped.reduce((a, b) => a + b, 0);
  }

  get fulfillmentActivatedSum(): number {
    return this.fulfillmentActivated.reduce((a, b) => a + b, 0);
  }

  setPage(n: number) {
    const max = this.devicePageCount;
    this.devicePage = Math.min(Math.max(1, n), max);
  }

  openDeviceDetails(device: any) {
    this.selectedDevice = device;
    this.isSidebarOpen = true;
  }

  closeDeviceDetails() {
    this.isSidebarOpen = false;
  }

  private initCharts() {
    if (this.donutRef?.nativeElement) {
      this.donutChart?.destroy();
      this.donutChart = new Chart(this.donutRef.nativeElement, {
        type: 'doughnut',
        data: {
          labels: ['Good', 'At Risk', 'Attention Required'],
          datasets: [
            {
              data: [
                this.deviceStatusSummary.good,
                this.deviceStatusSummary.atRisk,
                this.deviceStatusSummary.attention,
              ],
              backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '68%',
          plugins: { legend: { display: false } },
        },
      });
    }

    if (this.productRef?.nativeElement) {
      this.productChart?.destroy();
      this.productChart = new Chart(this.productRef.nativeElement, {
        type: 'bar',
        data: {
          labels: [''],
          datasets: this.productBreakdown.map((p) => ({
            label: p.code,
            data: [p.count],
            backgroundColor: p.color,
            borderWidth: 0,
            barPercentage: 1,
            categoryPercentage: 1,
          })),
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: true,
              max: this.totalDevices,
              grid: { display: false },
              ticks: { display: false },
            },
            y: { stacked: true, display: false },
          },
          plugins: { legend: { display: false } },
        },
      });
    }

    if (this.fulfillmentRef?.nativeElement) {
      this.fulfillmentChart?.destroy();
      this.fulfillmentChart = new Chart(this.fulfillmentRef.nativeElement, {
        type: 'bar',
        data: {
          labels: this.fulfillmentMonths,
          datasets: [
            {
              label: 'Shipped',
              data: this.fulfillmentShipped,
              backgroundColor: '#3b7dd6',
              borderRadius: 4,
            },
            {
              label: 'Activated',
              data: this.fulfillmentActivated,
              backgroundColor: '#10b981',
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: { top: 4 },
          },
          scales: {
            x: {
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { display: false },
            },
          },
          plugins: {
            legend: { display: false },
          },
        },
        plugins: [verticalBarOutsideValueLabels],
      });
    }

    if (this.configRef?.nativeElement) {
      this.configChart?.destroy();
      this.configChart = new Chart(this.configRef.nativeElement, {
        type: 'bar',
        data: {
          labels: this.configDistribution.map((c) => c.label),
          datasets: [
            {
              data: this.configDistribution.map((c) => c.count),
              backgroundColor: this.configDistribution.map((c) => c.color),
              borderRadius: 4,
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: { right: 8 },
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: { display: false },
            },
            y: {
              grid: { display: false },
            },
          },
          plugins: { legend: { display: false } },
        },
        plugins: [horizontalBarOutsideValueLabels],
      });
    }
  }

  private updateConfigChart() {
    if (!this.configChart) {
      return;
    }
    const dist = this.configDistribution;
    this.configChart.data.labels = dist.map((c) => c.label);
    const ds = this.configChart.data.datasets[0] as {
      data: number[];
      backgroundColor: string[];
    };
    ds.data = dist.map((c) => c.count);
    ds.backgroundColor = dist.map((c) => c.color);
    this.configChart.update();
  }

  toggleFleetDropdown(): void {
    this.fleetDropdownOpen = !this.fleetDropdownOpen;
  }

  selectFleet(option: string | number | Array<string | number>): void {
    if (typeof option !== "string") return;
    this.selectedFleet = option;
    this.fleetDropdownOpen = false;
  }
}
